/**
 * pages/GoalDetail.tsx — one objective's record, and every check-in ever run against it.
 *
 * **The lede is not decoration.** `06-modules.md` §5.3 R1 requires a record synchronised from another
 * system to state its source and its last sync *beside the record*, and to not present a synchronised
 * figure with the confidence of a live one. The objective's name is the `<h1>`; the owner and the sync
 * are the line directly beneath it, in that order, because a reader deciding whether to trust the owner
 * needs to know when we last asked.
 *
 * **Starting a check-in is a navigation, not the write.** `POST /goals/:id/checkins` is issued by the
 * flow screen on arrival, and it is the same call whether one is open or not — the server hands back the
 * open one with `resumed: true` rather than making a second. Splitting the call across two screens would
 * mean a person who pressed Start and then closed the tab had opened a check-in with nothing on screen
 * to resume it from; putting it on the screen that displays it means arriving IS resuming.
 */
import {
  fetchGoalCheckins,
  goalErrorMessage,
  type CheckinView,
  type GoalCheckinsResponse,
} from "../lib/api";
import { formatDateTime, formatRelative, humanise } from "../lib/format";
import { isSettled, orNotRead, useAsync } from "./goalsShared";
import {
  Badge,
  Banner,
  Button,
  Card,
  DeniedState,
  ErrorState,
  LoadingState,
  Page,
  Table,
  type Column,
  type Tone,
} from "../components/ui";

export interface GoalDetailPorts {
  readonly load: (signal: AbortSignal) => Promise<GoalCheckinsResponse>;
}

export interface GoalDetailProps {
  readonly buId: string;
  readonly goalId: string;
  /** From `/api/me`'s per-scope roles. Hiding is UX; the server re-checks `job_author` on the write. */
  readonly canAuthor?: boolean;
  readonly ports?: GoalDetailPorts;
}

const CHECKIN_TONE: Readonly<Record<string, Tone>> = {
  open: "info",
  completed: "success",
  abandoned: "neutral",
};

export function GoalDetail({ buId, goalId, canAuthor = false, ports }: GoalDetailProps): JSX.Element {
  const load = ports?.load ?? ((signal: AbortSignal) => fetchGoalCheckins(goalId, signal));
  const record = useAsync(load, `goal:${buId}:${goalId}`);

  const data = record.data;
  const goal = data?.goal ?? null;
  const connection = data?.connection ?? null;
  const open = data?.open ?? null;

  const checkinHref = `#/goals/${encodeURIComponent(goalId)}/checkin`;

  const closeQuarterHref = `#/goals/${encodeURIComponent(goalId)}/close-quarter`;

  /*
   * Two actions, and the destructive one is SECONDARY and second.
   *
   * Close Quarter creates goals and archives an objective with everything under it; Start check-in
   * posts a sentence. Giving them equal weight in the page header would put the quarter-ending button
   * one mis-aimed tap from the fortnightly one. The confirmation on the far side is the real control —
   * this is only the ordering that stops it being reached by accident.
   */
  const startButton =
    canAuthor && data !== null ? (
      <>
        <Button variant="primary" onClick={() => (window.location.hash = checkinHref)}>
          {open === null ? "Start check-in" : "Resume check-in"}
        </Button>
        {/* Sentence case, per 05-content.md §1 — and matching the screen it opens, which is titled
            "Close quarter" and whose action reads "Close the quarter". This was the only surface in the
            flow spelling it with a capital Q, beside "Start check-in" in the same header. */}
        <Button variant="secondary" onClick={() => (window.location.hash = closeQuarterHref)}>
          Close quarter
        </Button>
      </>
    ) : undefined;

  const columns: readonly Column<CheckinView>[] = [
    {
      key: "startedAt",
      header: "Started",
      type: "date",
      value: (row) => row.startedAt,
      render: (row) => <time title={formatDateTime(row.startedAt)}>{formatRelative(row.startedAt)}</time>,
    },
    {
      key: "status",
      header: "Outcome",
      type: "text",
      value: (row) => row.status,
      render: (row) => <Badge tone={CHECKIN_TONE[row.status] ?? "neutral"}>{humanise(row.status)}</Badge>,
    },
    {
      key: "endedAt",
      header: "Ended",
      type: "date",
      value: (row) => row.endedAt,
      // An open check-in has no end because it has not ended — a fact, so the em dash would be wrong
      // here in the other direction: it would read as "we do not know".
      render: (row) =>
        row.endedAt === null ? (
          row.status === "open" ? (
            "Still open"
          ) : (
            "Not recorded"
          )
        ) : (
          <time title={formatDateTime(row.endedAt)}>{formatRelative(row.endedAt)}</time>
        ),
    },
    {
      key: "actorEmail",
      header: "Run by",
      type: "text",
      value: (row) => row.actorEmail,
      render: (row) => orNotRead(row.actorEmail),
    },
  ];

  /**
   * §5.3 R1, in the one place a reader cannot miss it. Built as a string because `Page`'s lede is one —
   * the absolute timestamp lives in the record card below, where it can carry a `title`.
   *
   * ⚠️ **The clause and nothing beyond it**, on the owner's instruction of 10 August 2026. It read
   * "`KEY` · owner `NAME` · a snapshot read from `SITE`, last synchronised `WHEN`" and ran to three lines
   * on a phone, of which the owner and the word *snapshot* were the two the card immediately below already
   * carried — R2's own region, field by field, with the absolute instant on a `title`. What R1 asks for is
   * the identifier, the source and the last sync; those three stay, in one line. Do not restore the owner
   * here: a fact stated twice within a screen's height is the thing that pushed the record card itself
   * below the fold.
   */
  const lede =
    goal === null
      ? undefined
      : `${goal.key} · synchronised ${formatRelative(goal.syncedAt)} from ${connection?.site ?? "Atlassian"}`;

  return (
    <Page
      title={goal?.name ?? "Objective"}
      {...(lede === undefined ? {} : { lede })}
      {...(startButton === undefined ? {} : { actions: startButton })}
    >
      <p>
        <Button variant="link" size="sm" onClick={() => (window.location.hash = "#/goals")}>
          ← All goals
        </Button>
      </p>

      {record.loading ? <LoadingState label="Loading this objective" /> : null}

      {record.error !== null ? (
        record.error.isPermissionDenied ? (
          <DeniedState body={goalErrorMessage(record.error)} />
        ) : (
          <ErrorState
            message={goalErrorMessage(record.error)}
            traceId={record.error.traceId}
            onRetry={record.reload}
          />
        )
      ) : null}

      {connection === null && data !== null ? (
        <Banner tone="warning">
          This business unit is not connected to Atlassian Goals, so this objective cannot be re-read and
          no check-in can be posted. What is shown below is the last snapshot taken.
        </Banner>
      ) : null}

      {goal !== null ? (
        <Card title="Objective">
          <div className="stack">
            <p>
              <strong>Key</strong>{" "}
              <a href={goal.url} target="_blank" rel="noopener noreferrer">
                {goal.key}
              </a>
            </p>
            <p>
              <strong>Owner</strong> {orNotRead(goal.ownerName)}
            </p>
            <p>
              <strong>Last synchronised</strong>{" "}
              <time title={formatDateTime(goal.syncedAt)}>{formatRelative(goal.syncedAt)}</time>
              {connection === null ? null : <> from {connection.site}</>}
            </p>
            <p>
              <strong>Added here</strong>{" "}
              <time title={formatDateTime(goal.addedAt)}>{formatRelative(goal.addedAt)}</time>
            </p>
            {/* ⚠️ A hint explaining what a key result is, and that they are re-read when a check-in
                starts, stood here until 10 August 2026 and was removed on the owner's instruction. It is
                not load-bearing for any clause — R2 asks for the record's own fields, and none of the four
                above is a key result. The behaviour it described is unchanged and is stated where it can
                still be acted on: the *Add a goal* dialog on the list screen, and that screen's own empty
                state. Do not put it back on the grounds that a reader might wonder. */}
          </div>
        </Card>
      ) : null}

      {open !== null ? (
        <Card
          title="A check-in is already open"
          support={`${open.items.filter(isSettled).length} of ${open.items.length} key result(s) settled — started ${formatRelative(open.startedAt)} by ${orNotRead(open.actorEmail)}.`}
          actions={
            canAuthor ? (
              <Button variant="primary" onClick={() => (window.location.hash = checkinHref)}>
                Resume check-in
              </Button>
            ) : undefined
          }
        >
          <p className="field__hint">
            Only one check-in runs on an objective at a time, so that two people cannot post their own
            sentence to the same key result minutes apart with no way afterwards to tell which was meant.
          </p>
        </Card>
      ) : null}

      {data !== null ? (
        <Card title="Check-in history">
          <Table
            caption="Check-ins"
            columns={columns}
            rows={data.history}
            rowKey={(row) => row.id}
            defaultSort={{ key: "startedAt", direction: "descending" }}
            titleKey="startedAt"
            mobile="cards"
            empty={{
              title: "No check-in has been run on this objective yet",
              body: canAuthor
                ? "Starting one reads this objective's key results from Atlassian and walks them one at a time. Nothing is posted until you have read the exact sentence and said go."
                : "Running a check-in needs the goal-author role in this business unit; an administrator grants it through a group.",
              ...(startButton === undefined ? {} : { action: startButton }),
            }}
          />
        </Card>
      ) : null}
    </Page>
  );
}
